import React, { useEffect, useState, useCallback } from 'react';
import { pipelineHealthAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Activity, TrendingUp, DollarSign, Target, Users, RefreshCw, Loader2,
  Brain, Sparkles, ChevronDown, ChevronUp, BarChart3, Zap,
  AlertTriangle, Lightbulb, Rocket, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Area, AreaChart, Funnel, FunnelChart
} from 'recharts';

const FUNNEL_COLORS = ['hsl(204,94%,40%)', 'hsl(204,80%,50%)', 'hsl(171,66%,35%)', 'hsl(27,90%,52%)', 'hsl(40,90%,52%)', 'hsl(4,82%,55%)', 'hsl(130,50%,40%)'];
const INSIGHT_STYLES = {
  health: { icon: CheckCircle, bg: 'bg-emerald-50', border: 'border-emerald-200/60', text: 'text-emerald-700' },
  risk: { icon: AlertTriangle, bg: 'bg-rose-50', border: 'border-rose-200/60', text: 'text-rose-700' },
  opportunity: { icon: TrendingUp, bg: 'bg-sky-50', border: 'border-sky-200/60', text: 'text-sky-700' },
  action: { icon: Rocket, bg: 'bg-amber-50', border: 'border-amber-200/60', text: 'text-amber-700' },
};

export default function PipelineHealthDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentary, setCommentary] = useState(null);
  const [commentaryLoading, setCommentaryLoading] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [planRequired, setPlanRequired] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pipelineHealthAPI.getAnalytics();
      setData(res.data);
    } catch (err) {
      // Iter 37: 403 means tenant plan does not include this analytic — show upgrade card
      if (err?.response?.status === 403) {
        setPlanRequired(true);
      } else {
        toast.error('Failed to load pipeline data');
      }
    }
    finally { setLoading(false); }
  }, []);

  const fetchCommentary = useCallback(async () => {
    setCommentaryLoading(true);
    try {
      const res = await pipelineHealthAPI.getAICommentary();
      setCommentary(res.data);
    } catch { toast.error('Failed to get AI commentary'); }
    finally { setCommentaryLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-96" data-testid="pipeline-loading">
      <Loader2 className="w-7 h-7 animate-spin text-[hsl(204,94%,40%)]" />
    </div>
  );

  if (planRequired) return (
    <div className="flex items-center justify-center h-96 p-8" data-testid="pipeline-plan-required">
      <div className="max-w-md text-center space-y-4 p-8 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40">
        <Activity className="w-10 h-10 text-amber-600 dark:text-amber-400 mx-auto" />
        <h2 className="text-xl font-semibold">Pipeline Health is a Growth feature</h2>
        <p className="text-sm text-muted-foreground">
          Upgrade to the Growth plan to unlock real-time funnel analytics, conversion breakdowns,
          and AI-powered pipeline commentary.
        </p>
        <a
          href="/billing?tab=plans"
          className="inline-block px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
          data-testid="pipeline-upgrade-cta"
        >
          See Growth plan
        </a>
      </div>
    </div>
  );

  if (!data) return null;

  const { overview, funnel, conversions, source_breakdown, daily_trend } = data;
  const funnelData = funnel.filter(f => !f.is_won && !f.is_lost && f.count > 0);

  return (
    <div className="space-y-8 p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="pipeline-health-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-2 rounded-xl bg-[hsl(204,94%,40%)]/10">
              <Activity className="w-5 h-5 text-[hsl(204,94%,40%)]" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Pipeline Health
            </h1>
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            Historical trends and conversion analytics
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-pipeline-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
        <KPI icon={Users} label="Total Leads" value={overview.total_leads} testId="kpi-total-leads" />
        <KPI icon={Activity} label="Active" value={overview.active_leads} testId="kpi-active" variant="primary" />
        <KPI icon={Target} label="Win Rate" value={`${overview.win_rate}%`} testId="kpi-win-rate" variant="highlight" />
        <KPI icon={DollarSign} label="Pipeline" value={`$${(overview.pipeline_value / 1000).toFixed(0)}k`} testId="kpi-pipeline" variant="accent" sub={`Weighted: $${(overview.weighted_pipeline / 1000).toFixed(0)}k`} />
        <KPI icon={TrendingUp} label="Avg Won" value={`$${overview.avg_won_value.toLocaleString()}`} testId="kpi-avg-won" variant="success" />
      </div>

      {/* Pipeline Funnel */}
      <Card className="border-0 shadow-sm" data-testid="pipeline-funnel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Pipeline Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          {funnel.length > 0 ? (
            <div className="space-y-2">
              {funnel.map((stage, i) => {
                const maxCount = Math.max(...funnel.map(f => f.count), 1);
                const width = Math.max((stage.count / maxCount) * 100, 8);
                return (
                  <div key={stage.stage} className="flex items-center gap-3" data-testid={`funnel-stage-${i}`}>
                    <span className="text-xs text-muted-foreground w-32 text-right shrink-0 truncate">{stage.stage}</span>
                    <div className="flex-1 relative h-8 rounded-lg overflow-hidden bg-muted/40">
                      <div
                        className="h-full rounded-lg transition-all duration-500 flex items-center px-3"
                        style={{ width: `${width}%`, backgroundColor: FUNNEL_COLORS[i % FUNNEL_COLORS.length], opacity: 0.85 }}
                      >
                        <span className="text-xs font-medium text-white">{stage.count}</span>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-20 shrink-0">
                      ${(stage.value / 1000).toFixed(0)}k
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState msg="No pipeline data" />
          )}
        </CardContent>
      </Card>

      {/* Conversions + Win/Loss Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-0 shadow-sm" data-testid="conversion-rates">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Zap className="w-4 h-4" /> Stage Conversion Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {conversions.length > 0 ? (
              <div className="space-y-3">
                {conversions.map((c, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`conversion-${i}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-xs mb-1">
                        <span className="text-muted-foreground">{c.from_stage}</span>
                        <span className="text-muted-foreground/50">→</span>
                        <span className="font-medium">{c.to_stage}</span>
                      </div>
                      <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-[hsl(204,94%,40%)] transition-all" style={{ width: `${Math.min(c.rate, 100)}%` }} />
                      </div>
                    </div>
                    <span className="text-sm font-semibold w-12 text-right" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {c.rate}%
                    </span>
                  </div>
                ))}
              </div>
            ) : <EmptyState msg="Need more stage data" />}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="win-loss-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Target className="w-4 h-4" /> Won vs Lost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-8 py-6">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {overview.won}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Won</p>
              </div>
              <div className="text-4xl text-muted-foreground/20 font-light">/</div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-rose-50 flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-rose-600" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {overview.lost}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Lost</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Win rate: <span className="font-semibold text-foreground">{overview.win_rate}%</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Trend */}
      {daily_trend.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="daily-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Daily Lead Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={daily_trend}>
                <defs>
                  <linearGradient id="gNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(204,94%,40%)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="hsl(204,94%,40%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12 }} />
                <Area type="monotone" dataKey="new" name="New Leads" stroke="hsl(204,94%,40%)" fill="url(#gNew)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* AI Commentary */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="ai-commentary-panel">
        <div className="h-0.5 bg-gradient-to-r from-[hsl(204,94%,40%)] via-[hsl(171,66%,35%)] to-[hsl(27,90%,52%)]" />
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Brain className="w-4 h-4 text-[hsl(204,94%,40%)]" /> AI Pipeline Commentary
            </CardTitle>
            <Button variant="outline" size="sm" onClick={fetchCommentary} disabled={commentaryLoading} className="gap-1.5" data-testid="get-commentary-btn">
              {commentaryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {commentary ? 'Refresh' : 'Analyze'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {commentaryLoading ? (
            <div className="flex items-center justify-center py-10 gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[hsl(204,94%,40%)]" />
              <span className="text-sm text-muted-foreground">Brain is analyzing your pipeline...</span>
            </div>
          ) : commentary?.insights?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {commentary.insights.map((ins, i) => {
                const s = INSIGHT_STYLES[ins.type] || INSIGHT_STYLES.action;
                const Icon = s.icon;
                return (
                  <div key={i} className={`p-3.5 rounded-xl border ${s.border} ${s.bg} hover:shadow-sm transition-all`} data-testid={`commentary-${i}`}>
                    <div className="flex items-start gap-2.5">
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} />
                      <div>
                        <h4 className={`text-sm font-semibold ${s.text} mb-1`}>{ins.title}</h4>
                        <p className="text-xs text-muted-foreground">{ins.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Brain className="w-8 h-8 text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">Get AI analysis of your pipeline health</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Source Breakdown (collapsible) */}
      {source_breakdown.length > 0 && (
        <div>
          <button onClick={() => setShowSources(!showSources)} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3" data-testid="toggle-sources">
            {showSources ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Source Performance ({source_breakdown.length} sources)
          </button>
          {showSources && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              {source_breakdown.map((s, i) => (
                <Card key={s.source} className="border-0 shadow-sm" data-testid={`source-${i}`}>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium mb-2">{s.source}</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div><p className="text-lg font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{s.total}</p><p className="text-[10px] text-muted-foreground">Total</p></div>
                      <div><p className="text-lg font-semibold text-emerald-600" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{s.won}</p><p className="text-[10px] text-muted-foreground">Won</p></div>
                      <div><p className="text-lg font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{s.win_rate}%</p><p className="text-[10px] text-muted-foreground">Win Rate</p></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, testId, variant, sub }) {
  const colors = {
    primary: 'bg-[hsl(204,94%,40%)]/10 text-[hsl(204,94%,40%)]',
    accent: 'bg-[hsl(171,66%,35%)]/10 text-[hsl(171,66%,35%)]',
    highlight: 'bg-emerald-50 text-emerald-600',
    success: 'bg-emerald-50 text-emerald-600',
    default: 'bg-muted text-muted-foreground',
  };
  const c = (colors[variant] || colors.default).split(' ');
  return (
    <Card className={`border-0 shadow-sm ${variant === 'highlight' ? 'ring-1 ring-emerald-200/50' : ''}`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-1.5 rounded-lg ${c[0]}`}><Icon className={`w-3.5 h-3.5 ${c[1]}`} /></div>
          <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest">{label}</span>
        </div>
        <p className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="flex flex-col items-center justify-center h-32 text-muted-foreground/50">
      <BarChart3 className="w-6 h-6 mb-1" /><p className="text-xs">{msg}</p>
    </div>
  );
}
